import { create } from 'zustand'

import { comparisonService } from '@/modules/comparison/services'
import type {
  ComparisonFilters,
  ComparisonHistoryEntry,
  ComparisonSortBy,
  ComparisonStatistics,
  ComparisonSummary,
  ProductComparisonGroup,
  RankedOffer,
  RankingWeights,
} from '@/modules/comparison/types'
import { DEFAULT_RANKING_WEIGHTS } from '@/modules/comparison/types'

interface ComparisonStoreState {
  ready: boolean
  query: string
  filters: ComparisonFilters
  sortBy: ComparisonSortBy
  groups: ProductComparisonGroup[]
  flat: RankedOffer[]
  summary: ComparisonSummary
  selectedIds: string[]
  favorites: string[]
  history: ComparisonHistoryEntry[]
  statistics: ComparisonStatistics
  weights: RankingWeights

  hydrate: () => void
  setQuery: (query: string) => void
  setFilters: (filters: Partial<ComparisonFilters>) => void
  setSortBy: (sortBy: ComparisonSortBy) => void
  runSearch: (term?: string, options?: { persistHistory?: boolean }) => void
  toggleSelect: (offerId: string) => void
  clearSelection: () => void
  toggleFavorite: (supplier: string) => void
  saveWeights: (weights: RankingWeights) => void
  refreshStats: () => void
}

const emptySummary: ComparisonSummary = {
  totalOffers: 0,
  groups: 0,
  recommendedSupplier: null,
  economy: 0,
  priceDiff: 0,
  bestPrice: null,
  bestScore: null,
}

export const useComparisonStore = create<ComparisonStoreState>((set, get) => ({
  ready: false,
  query: '',
  filters: { inStockOnly: false },
  sortBy: 'score_desc',
  groups: [],
  flat: [],
  summary: emptySummary,
  selectedIds: [],
  favorites: [],
  history: [],
  statistics: comparisonService.statistics(),
  weights: DEFAULT_RANKING_WEIGHTS,

  hydrate: () => {
    set({
      ready: true,
      favorites: comparisonService.listFavorites(),
      history: comparisonService.listHistory(),
      statistics: comparisonService.statistics(),
      weights: comparisonService.getWeights(),
    })
  },

  setQuery: (query) => set({ query }),

  setFilters: (filters) => {
    set({ filters: { ...get().filters, ...filters } })
    get().runSearch(undefined, { persistHistory: false })
  },

  setSortBy: (sortBy) => {
    set({ sortBy })
    get().runSearch(undefined, { persistHistory: false })
  },

  runSearch: (term, options) => {
    const query = term ?? get().query
    const persistHistory = options?.persistHistory ?? Boolean(term?.trim())
    const result = comparisonService.searchAndCompare(query, get().filters, get().sortBy, {
      persistHistory,
    })
    set({
      query,
      groups: result.groups,
      flat: result.flat,
      summary: result.summary,
      history: comparisonService.listHistory(),
      statistics: comparisonService.statistics(),
      ready: true,
    })
  },

  toggleSelect: (offerId) => {
    const current = get().selectedIds
    if (current.includes(offerId)) {
      set({ selectedIds: current.filter((id) => id !== offerId) })
      return
    }
    if (current.length >= 4) return
    set({ selectedIds: [...current, offerId] })
  },

  clearSelection: () => set({ selectedIds: [] }),

  toggleFavorite: (supplier) => {
    const favorites = comparisonService.toggleFavorite(supplier)
    set({ favorites })
  },

  saveWeights: (weights) => {
    const saved = comparisonService.saveWeights(weights)
    set({ weights: saved })
    get().runSearch()
  },

  refreshStats: () => set({ statistics: comparisonService.statistics() }),
}))
